(($) => {
    const isBlobSliceSupported = window.Blob &&
        (Blob.prototype.slice || Blob.prototype.webkitSlice || Blob.prototype.mozSlice);

    function blobSlice(file, start, length) {
        const slice = file.slice || file.webkitSlice || file.mozSlice;

        return slice.apply(file, [start, start + length]);
    }

    function copyFormDataAndAppend(formData, appendData) {
        const newFormData = new FormData();

        Array.from(
            formData instanceof FormData
                ? formData.keys()
                : Object.keys(formData))
            .forEach((key) => newFormData.append(
                key,
                formData instanceof FormData
                    ? formData.get(key)
                    : formData[key]));

        if (appendData) {
            Object.keys(appendData).forEach((key) => newFormData.append(key, appendData[key]));
        }

        return newFormData;
    }

    function fileUpload(fileName, fileData, url, formData, options, chunkOptions) {
        const reportProgress = (loaded, total) => {
            if (!chunkOptions?.chunk && options.uploadProgress) {
                options.uploadProgress({
                    loaded,
                    total,
                });
            }

            if (chunkOptions?.uploadProgress) {
                chunkOptions.uploadProgress({
                    loaded,
                    total,
                });
            }
        };

        return new Promise((resolve, reject) => {
            let queryUrl = url;
            if (options.query) {
                const queryParams = Object.keys(options.query)
                    .map((key) => `${key}=${options.query[key]}`)
                    .join('&');
                queryUrl = `${url}?${queryParams}`;
            }

            const requestData = copyFormDataAndAppend(
                formData,
                {
                    [options.fileKey || fileName]: fileData,
                });

            reportProgress(0, fileData.size);

            const request = $.post({
                url: queryUrl,
                dataType: 'json',
                async: true,
                contentType: false,
                processData: false,
                data: requestData,
                headers: options.headers,
                xhr: () => {
                    const xmlHttpRequest = $.ajaxSettings.xhr();
                    xmlHttpRequest.upload.addEventListener('progress', (event) => {
                        reportProgress(event.loaded, event.total);
                    });

                    return xmlHttpRequest;
                },
                success: (result) => {
                    reportProgress(fileData.size, fileData.size);
                    resolve(result);
                },
                error: reject,
            });

            if (!chunkOptions?.chunk && options.abortUpload) {
                options.abortUpload(() => request.abort());
            }

            if (chunkOptions?.abortUpload) {
                chunkOptions.abortUpload(() => request.abort());
            }
        });
    }

    function chunkedFileUpload(file, url, formData, maxUploadChunkSizeBytes, uploadId, options) {
        const reportProgress = (loaded, total) => {
            if (options.uploadProgress) {
                options.uploadProgress({
                    loaded,
                    total,
                });
            }
        };

        return new Promise((resolve, reject) => {
            options.headers = options.headers || {};

            reportProgress(0, file.size);

            let fileUploadAbort;
            let abort = false;
            if (options.abortUpload) {
                options.abortUpload(() => {
                    abort = true;
                    if (fileUploadAbort) {
                        fileUploadAbort();
                    }
                });
            }

            (function startUpload(start) {
                const chunkSize = Math.min(file.size - start, maxUploadChunkSizeBytes);
                const fileData = new File(
                    [blobSlice(file, start, chunkSize)],
                    file.name,
                    {
                        type: file.type,
                        lastModified: file.lastModified,
                    });
                const uploadFormData = copyFormDataAndAppend(
                    formData,
                    {
                        __chunkedFileUploadId: uploadId,
                    });

                options.headers['Content-Range'] = `bytes ${start}-${start + chunkSize - 1}/${file.size}`;
                fileUpload(
                    file.name,
                    fileData,
                    url,
                    uploadFormData,
                    options,
                    {
                        chunk: true,
                        abortUpload: (uploadAbort) => {
                            fileUploadAbort = uploadAbort;
                        },
                        uploadProgress: (progress) => {
                            reportProgress(start + progress.loaded, file.size);
                        },
                    })
                    .then((response) => {
                        if (abort) {
                            reject(new Error('Upload aborted.'));
                        }

                        reportProgress(start + chunkSize, file.size);

                        if (start + chunkSize < file.size) {
                            return startUpload(start + chunkSize);
                        }

                        return resolve(response);
                    })
                    .catch(reject);
            })(0);
        });
    }

    $.extend({
        chunkedFileUpload(file, url, formData, maxUploadChunkSizeBytes, uploadId, options) {
            return new Promise((resolve, reject) => {
                const result = maxUploadChunkSizeBytes && isBlobSliceSupported
                    ? chunkedFileUpload(file, url, formData, maxUploadChunkSizeBytes, uploadId, options || { })
                    // Fallback to normal upload.
                    : fileUpload(file.name, file, url, formData, options || { });
                result
                    .then(resolve)
                    .catch(reject);
            });
        },
    });
})(jQuery);
